import { REPORT_MAPPINGS } from 'dashboard/lib/constants/report-mappings';
import { Case } from 'dashboard/models/case';
import { ReportAddressLookup } from 'dashboard/models/report/address-lookup';
import {
  AdverseMediaIgnoreListEntry,
  ReportAdverseMedia,
} from 'dashboard/models/report/adverse-media';
import { ReportBusinessAdverseMedia } from 'dashboard/models/report/business-adverse-media';
import { ReportBusinessAssociatedPersons } from 'dashboard/models/report/business-associated-persons';
import { ReportBusinessClassification } from 'dashboard/models/report/business-classification';
import { ReportBusinessIndustryClassification } from 'dashboard/models/report/business-industry-classification';
import { ReportBusinessLiensLookup } from 'dashboard/models/report/business-liens-lookup';
import { ReportBusinessLookup } from 'dashboard/models/report/business-lookup';
import { ReportBusinessNonprofit } from 'dashboard/models/report/business-nonprofit';
import { ReportBusinessOnlinePresence } from 'dashboard/models/report/business-online-presence';
import { ReportBusinessRegistrationsLookup } from 'dashboard/models/report/business-registrations-lookup';
import { ReportBusinessSocialMedia } from 'dashboard/models/report/business-social-media';
import { ReportBusinessWatchlist } from 'dashboard/models/report/business-watchlist';
import { ReportBusinessWebsite } from 'dashboard/models/report/business-website';
import { ReportCryptoAddressWatchlist } from 'dashboard/models/report/crypto-address-watchlist';
import { ReportCustomList } from 'dashboard/models/report/custom-list';
import { ReportEmailAddress } from 'dashboard/models/report/email-address';
import { ReportFinraBrokerCheck } from 'dashboard/models/report/finra-broker-check';
import { ReportChainalysisAddressScreening } from 'dashboard/models/report/integration/chainalysis-address-screening';
import { ReportClearbitBusinessLookup } from 'dashboard/models/report/integration/clearbit-business-lookup';
import { ReportCoinbaseCheckCryptoRisk } from 'dashboard/models/report/integration/coinbase-check-crypto-risk';
import { ReportEquifaxOneview } from 'dashboard/models/report/integration/equifax-oneview';
import { ReportKyckrBusinessLookup } from 'dashboard/models/report/integration/kyckr-business-lookup';
import { ReportMiddeskBusinessLookup } from 'dashboard/models/report/integration/middesk-business-lookup';
import { ReportMxAccount } from 'dashboard/models/report/integration/mx-account';
import { ReportSentilinkApplicationRisk } from 'dashboard/models/report/integration/sentilink-application-risk';
import { ReportSentilinkScores } from 'dashboard/models/report/integration/sentilink-scores';
import { ReportTrmWalletScreening } from 'dashboard/models/report/integration/trm-wallet-screening';
import { ReportPhoneNumber } from 'dashboard/models/report/phone-number';
import { ReportPoliticallyExposedPerson } from 'dashboard/models/report/politically-exposed-person';
import { ReportProfile } from 'dashboard/models/report/profile';
import { ReportProfileNonAuthoritative } from 'dashboard/models/report/profile-non-authoritative';
import { ReportScreening } from 'dashboard/models/report/screening';
import { ReportSecActionLookup } from 'dashboard/models/report/sec-action-lookup';
import { ReportSocialMedia } from 'dashboard/models/report/social-media';
import { ReportSynthetic } from 'dashboard/models/report/synthetic';
import { ReportWatchlist, WatchlistIgnoreListEntry } from 'dashboard/models/report/watchlist';
import {
  ReportSnapshotAdverseMedia,
  ReportSnapshotBusinessNonprofit,
  ReportSnapshotWatchlist,
} from 'dashboard/models/report-snapshot';
import { ReportTemplate } from 'dashboard/models/report-template';
import { ApiListRelationship, ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';
import { Iso8601DateTimeString } from 'lib/types';

export enum ReportStatus {
  Errored = 'errored',
  Pending = 'pending',
  Ready = 'ready',
  Restricted = 'restricted',
  Submitted = 'submitted',
}

export enum ReportMatchResult {
  Matched = 'matched',
  NoMatches = 'no_matches',
}

export enum ReportRecurStatus {
  Active = 'active',
  Paused = 'paused',
}

export enum RequiredField {
  AddressStreet1 = 'address_street_1',
  AddressCity = 'address_city',
  AddressSubdivision = 'address_subdivision',
  AddressPostalCode = 'address_postal_code',
  AddressCountryCode = 'address_country_code',
  Birthdate = 'birthdate',
  CountryCode = 'country_code',
  EmailAddress = 'email_address',
  NameFirst = 'name_first',
  NameLast = 'name_last',
  PhoneNumber = 'phone_number',
  SocialSecurityNumber = 'social_security_number',
  Term = 'term',
}

export type IgnoreListEntry = WatchlistIgnoreListEntry | AdverseMediaIgnoreListEntry;

export type IgnoreType = 'entity' | 'media' | 'entity_matches' | 'all';

export interface ReportAttributes {
  'primary-input': string;
  status: ReportStatus;
  'created-at': Iso8601DateTimeString;
  'completed-at': Iso8601DateTimeString;
  'creator-token'?: string;
  'creator-type'?: string;
  'creator-name'?: string;
  'next-scheduled-at'?: Iso8601DateTimeString;
  'redacted-at'?: Iso8601DateTimeString;
  'parent-marked-for-cascading-redaction-at'?: Iso8601DateTimeString;
  'reference-id'?: string;
  'account-id'?: string;
  'environment-id'?: string;
  'report-template-id'?: string;
  'report-template-version-name'?: string;
  'report-template-version-interval-seconds'?: number;
  'missing-required-fields'?: RequiredField[];
  'failure-reason'?: string;
  'failure-message'?: string;
  'recur-status'?: ReportRecurStatus;
  'error-message'?: string;
  //Needed for marking hits as ignored
  'ignore-reason'?: string;
  'entity-id'?: string;
  'ignore-type'?: IgnoreType;
  'has-match'?: boolean;
}

export interface ReportMeta {
  'related-reports'?: RelatedReport[];
}

export interface RelatedReport {
  token: string;
  type: Report['type'];
}

export interface ReportRelationships {
  account?: ApiRelationship<ApiModel.Account>;
  cases?: ApiListRelationship<Case['type']>;
  'report-template'?: ApiRelationship<ReportTemplate['type']>;
  tags?: ApiListRelationship<ApiModel.Tag>;
}

export const SUPPORTED_REPORT_TYPES = [
  ApiModel.ReportAddressLookup,
  ApiModel.ReportAdverseMedia,
  ApiModel.ReportBusinessAdverseMedia,
  ApiModel.ReportBusinessAssociatedPersons,
  ApiModel.ReportBusinessClassification,
  ApiModel.ReportBusinessIndustryClassification,
  ApiModel.ReportBusinessRegistrationsLookup,
  ApiModel.ReportBusinessLiensLookup,
  ApiModel.ReportBusinessLookup,
  ApiModel.ReportBusinessNonprofit,
  ApiModel.ReportBusinessOnlinePresence,
  ApiModel.ReportBusinessSocialMedia,
  ApiModel.ReportBusinessWatchlist,
  ApiModel.ReportBusinessWebsite,
  ApiModel.ReportChainalysisAddressScreening,
  ApiModel.ReportCryptoAddressWatchlist,
  ApiModel.ReportCustomList,
  ApiModel.ReportEmailAddress,
  ApiModel.ReportEquifaxOneview,
  ApiModel.ReportFinraBrokerCheck,
  ApiModel.ReportKyckrBusinessLookup,
  ApiModel.ReportMxAccount,
  ApiModel.ReportPhoneNumber,
  ApiModel.ReportPoliticallyExposedPerson,
  ApiModel.ReportProfile,
  ApiModel.ReportProfileNonAuthoritative,
  ApiModel.ReportSecActionLookup,
  ApiModel.ReportSentilinkApplicationRisk,
  ApiModel.ReportSentilinkScores,
  ApiModel.ReportSocialMedia,
  ApiModel.ReportSynthetic,
  ApiModel.ReportTrmWalletScreening,
  ApiModel.ReportWatchlist,
];

export const DISMISSIBLE_REPORT_TYPES = [
  ApiModel.ReportAdverseMedia,
  ApiModel.ReportPoliticallyExposedPerson,
  ApiModel.ReportWatchlist,
];

export const RECURRING_REPORT_TYPES = [
  ApiModel.ReportAdverseMedia,
  ApiModel.ReportBusinessAdverseMedia,
  ApiModel.ReportBusinessNonprofit,
  ApiModel.ReportBusinessWatchlist,
  ApiModel.ReportCryptoAddressWatchlist,
  ApiModel.ReportCustomList,
  ApiModel.ReportPoliticallyExposedPerson,
  ApiModel.ReportWatchlist,
];

export const SNAPSHOT_VIEW_REPORT_TYPES = [
  ApiModel.ReportWatchlist,
  ApiModel.ReportAdverseMedia,
  ApiModel.ReportBusinessNonprofit,
];

/*
Reports contained within these types are made for the purposes
of KYC / AML screening. Having a "match" on these reports is
generally a _negative_ indicator, so we need to have different logic
when displaying UX elements on the report status.

Currently these are the same as recurring reports, but this can change
down the line.
*/
export const SCREENING_REPORT_TYPES = RECURRING_REPORT_TYPES;

export const BUSINESS_REPORT_TYPES = [
  ApiModel.ReportBusinessAdverseMedia,
  ApiModel.ReportBusinessAssociatedPersons,
  ApiModel.ReportBusinessClassification,
  ApiModel.ReportBusinessIndustryClassification,
  ApiModel.ReportBusinessLiensLookup,
  ApiModel.ReportBusinessLookup,
  ApiModel.ReportBusinessNonprofit,
  ApiModel.ReportBusinessOnlinePresence,
  ApiModel.ReportBusinessRegistrationsLookup,
  ApiModel.ReportBusinessSocialMedia,
  ApiModel.ReportBusinessWatchlist,
  ApiModel.ReportBusinessWebsite,
  ApiModel.ReportMiddeskBusinessLookup,
];

export const RECURRING_CA_REPORT_TYPES = [
  ApiModel.ReportAdverseMedia,
  ApiModel.ReportBusinessAdverseMedia,
  ApiModel.ReportBusinessWatchlist,
  // TODO (jason): Remove this when PEP is migrated to OS based in-house DB.
  ApiModel.ReportPoliticallyExposedPerson,
];

export const EXPORTABLE_REPORT_TYPES = [
  ApiModel.ReportPoliticallyExposedPerson,
  ApiModel.ReportWatchlist,
];

export type Report =
  // This first one is needed for any actions that are sent to the generic /reports endpoint
  | ApiResource<ReportAttributes, ReportRelationships, ApiModel.Report>
  | ReportAddressLookup
  | ReportAdverseMedia
  | ReportBusinessAdverseMedia
  | ReportBusinessAssociatedPersons
  | ReportBusinessClassification
  | ReportBusinessIndustryClassification
  | ReportBusinessLiensLookup
  | ReportBusinessLookup
  | ReportBusinessNonprofit
  | ReportBusinessOnlinePresence
  | ReportBusinessRegistrationsLookup
  | ReportBusinessSocialMedia
  | ReportBusinessWatchlist
  | ReportBusinessWebsite
  | ReportChainalysisAddressScreening
  | ReportClearbitBusinessLookup
  | ReportCoinbaseCheckCryptoRisk
  | ReportCryptoAddressWatchlist
  | ReportCustomList
  | ReportEmailAddress
  | ReportEquifaxOneview
  | ReportFinraBrokerCheck
  | ReportKyckrBusinessLookup
  | ReportMiddeskBusinessLookup
  | ReportMxAccount
  | ReportPhoneNumber
  | ReportPoliticallyExposedPerson
  | ReportProfile
  | ReportProfileNonAuthoritative
  | ReportScreening
  | ReportSecActionLookup
  | ReportSentilinkApplicationRisk
  | ReportSentilinkScores
  | ReportSnapshotAdverseMedia
  | ReportSnapshotBusinessNonprofit
  | ReportSnapshotWatchlist
  | ReportSocialMedia
  | ReportSynthetic
  | ReportTrmWalletScreening
  | ReportWatchlist;

export const REPORT_TYPE_TO_LABEL: Record<Report['type'], string> = Object.fromEntries(
  REPORT_MAPPINGS.map((r) => [[r.reportType], r.label]),
);
